import React, { useEffect, useState, useCallback } from 'react';
import apiClient from '../utils/api';
import { useAuth } from '../context/AuthContext';

const statusColor = (s: string) => s.toLowerCase();
const METHOD_LABELS: Record<string, string> = {
  CASH: 'Cash', BANK_TRANSFER: 'Bank Transfer', MOBILE_MONEY: 'Mobile Money', CARD: 'Card', OTHER: 'Other',
};
const LIMIT = 15;

export const Payments: React.FC = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState('');

  const canManage = user && ['SUPER_ADMIN', 'COMPANY_ADMIN', 'DISPATCH_AGENT', 'RECOVERY_OFFICER'].includes(user.role);
  const canFail = user && ['SUPER_ADMIN', 'COMPANY_ADMIN', 'RECOVERY_OFFICER'].includes(user.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      const res = await apiClient.listPayments(filters, LIMIT, page * LIMIT);
      const data = res.data.data;
      setPayments(data?.payments ?? data?.rows ?? (Array.isArray(data) ? data : []));
      setTotal(data?.total ?? data?.count ?? 0);
    } catch {/* silent */}
    finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async (id: string) => {
    setActionLoading(id);
    try { await apiClient.completePayment(id, {}); await load(); }
    catch (e: any) { setError(e.response?.data?.error || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const handleFail = async (id: string) => {
    const reason = window.prompt('Enter reason for failing the payment:');
    if (reason === null) return;
    setActionLoading(id);
    try { await apiClient.failPayment(id, reason); await load(); }
    catch (e: any) { setError(e.response?.data?.error || 'Failed'); }
    finally { setActionLoading(null); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Payments</h1>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 16 }}>{error}</div>}

      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ width: 180 }}>
            {['', 'PENDING', 'COMPLETED', 'FAILED', 'REFUNDED'].map(s => (
              <option key={s} value={s}>{s || 'All Statuses'}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="loading-spinner">Loading payments...</div>
        ) : payments.length === 0 ? (
          <div className="empty-state"><h3>No payments found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Shipment</th>
                    <th>Method</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Reference</th>
                    <th>Due Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p: any) => (
                    <tr key={p.id}>
                      <td style={{ fontSize: 12 }}>
                        <code>{p.Shipment?.trackingNumber || p.shipmentId?.slice(0, 8)}</code>
                      </td>
                      <td>{METHOD_LABELS[p.paymentMethod] || p.paymentMethod}</td>
                      <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{Number(p.amount).toLocaleString()} {p.currency}</td>
                      <td><span className={`badge badge-${statusColor(p.status)}`}>{p.status}</span></td>
                      <td>{p.transactionReference || '—'}</td>
                      <td>{p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '—'}</td>
                      <td>
                        {canManage && p.status === 'PENDING' && (
                          <>
                            <button
                              className="btn btn-success"
                              style={{ padding: '4px 10px', fontSize: 12, marginRight: 4 }}
                              onClick={() => handleComplete(p.id)}
                              disabled={actionLoading === p.id}
                            >
                              Complete
                            </button>
                            {canFail && (
                              <button
                                className="btn btn-danger"
                                style={{ padding: '4px 10px', fontSize: 12 }}
                                onClick={() => handleFail(p.id)}
                                disabled={actionLoading === p.id}
                              >
                                Fail
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
